import MainLayout from '@layouts/MainLayout';

import QuantityField from '@components/Forms/QuanityField';
import Breadcrumb from '@components/Common/Breadcrumb';

import styles from './style.module.css';
import {useRouter} from 'next/router';
import Button from '@components/Forms/Button';
import {useEffect} from 'react';

import {toThousand} from '@lib/utils';

import {useCart, useProductDisplay} from '@contexts/index';
import ProductDisplayTab from '../ProductDisplayTab';
import {useTranslation} from 'next-i18next';
import ProductDisplayImage from '../ProductDisplayImage';
import FormGroup from '@components/Forms/FormGroup';
import TextField from '@components/Forms/TextField';
import {LocaleEnum} from '@enums/locale';
import {ProductGroupDisplayResponseData} from '@interfaces/product-group-display';
import {ProductInterface} from '@interfaces/product';
import {ProductPrices} from '@contexts/actions';
import {CMSBreadcrumb} from '@interfaces/content-container';
import HeadMeta from '@components/Header/HeadMeta';

export default function SingleProductDisplayComponent({
  data,
}: {
  data: ProductGroupDisplayResponseData & {
    product: ProductInterface & ProductPrices;
  };
}) {
  const {t} = useTranslation('product-display');
  const router = useRouter();
  const {breadcrumb, images, tabs, content_container} = data;
  const locale = router.locale as LocaleEnum;
  const product_name = data.product[`product_name_${locale}`];
  const product_description = data.product[`product_description_${locale}`];
  const product_group_information = data[`product_group_information_${locale}`];
  const product_group_description = data[`product_group_description_${locale}`];

  const {
    state,
    setQuantity,
    setProduct,
    setProductPrices,
    getProductPrices,
    setKeyValuePairs,
  } = useProductDisplay();

  const {
    product,
    quantity,
    total_discount_price_vat_included,
    key_value_pairs,
    load_product_prices,
  } = state;

  const {
    addItemToCart,
    state: {addItemLoading},
  } = useCart();

  useEffect(() => {
    setProduct(data.product);
    setProductPrices(data.product);
  }, []);

  useEffect(() => {
    if (quantity && product?.id) {
      getProductPrices();
    }
  }, [load_product_prices, quantity]);

  const handleAddToCart = () => {
    const items = {
      product_id: product!.id,
      quantity,
      key_value_pairs,
      linked_products: [],
    };
    addItemToCart({items});
  };

  const productListPage = {
    fi: 'naytaliunetuoteperhelista',
    en: 'showliunefamilylist',
    se: 'visaliuneproduktfamiljlista',
  };

  return (
    <MainLayout>
      <HeadMeta
        title={
          data.content_container[`seo_page_title_${locale}`] ||
          data[`product_group_name_${locale}`]
        }
        description={data.content_container[`seo_description_${locale}`]}
        keywords={data.content_container[`seo_keywords_${locale}`]}
        canonical_url={data.content_container[`canonical_url_${locale}`]}
      />
      <Breadcrumb
        baseUrl={t<string>('product_base_url')}
        classNames={styles.productDisplayBreadcrumb}
        justifyStart={true}
        list={[
          ...breadcrumb.slice(0, breadcrumb.length - 1),
          {
            ...(breadcrumb[breadcrumb.length - 1] || {}),
            [`seo_friendly_url_${locale}`]: `${productListPage[locale]}/${
              breadcrumb[breadcrumb.length - 1][`seo_friendly_url_${locale}`]
            }`,
          } as CMSBreadcrumb,
          {['product_group_name_fi']: product_name} as unknown as CMSBreadcrumb,
        ]}
      />
      <div className="container">
        <div className={`${styles.productFamilyContainer} mb-5 `}>
          <div className="row position-relative">
            <div className="col-md-6 col-lg-7 sticky-md-top align-self-start">
              <ProductDisplayImage src={images[0]?.image_fi} />
            </div>
            <div className="col-md-6 col-lg-5">
              <div className={`${styles.productOverview}`}>
                <h1 title={`${product_name}`}>{product_name}</h1>

                <div className={`${styles.priceInformation} mb-2`}>
                  {product?.unit_price_vat_included !==
                    product?.discount_unit_price_vat_included && (
                    <h5
                      className={`${styles.priceSlash} ${
                        load_product_prices && styles.blurred
                      } `}
                    >
                      {toThousand(product?.unit_price_vat_included ?? 0)} €
                    </h5>
                  )}
                  <h4
                    className={`${
                      (load_product_prices || !product?.id) && styles.blurred
                    }`}
                  >
                    {toThousand(product?.discount_unit_price_vat_included ?? 0)}{' '}
                    €
                    <span className={styles.productVat}>
                      {' '}
                      /{' '}
                      {toThousand(
                        product?.discount_unit_price_vat_excluded ?? 0
                      )}{' '}
                      € {t<string>('vat_exclusive')}
                    </span>
                  </h4>
                </div>
                <div
                  className={`${styles.productInformation}`}
                  dangerouslySetInnerHTML={{
                    __html: product_description,
                  }}
                ></div>

                <div className={`row ${styles.productTotals}`}>
                  <div className={`${styles.priceInformation}`}>
                    <p> {t<string>('total_price')}:</p>
                    <h4
                      className={` ${
                        (load_product_prices || !product?.id) && styles.blurred
                      }`}
                    >
                      {toThousand(
                        Number(
                          total_discount_price_vat_included ||
                            product?.discount_unit_price_vat_included ||
                            0
                        )
                      )}{' '}
                      €
                    </h4>
                  </div>
                  <div className={`row ${styles.productTotals}`}>
                    <FormGroup>
                      <label htmlFor="">
                        {t<string>('additional_information')}
                      </label>
                      <TextField
                        placeholder={t<string>('additional_information')}
                        onChange={e => {
                          setKeyValuePairs({
                            [t<string>('additional_information')]:
                              e.target.value,
                          });
                        }}
                      />
                    </FormGroup>
                  </div>
                  {/*<div className={'mb-1'} style={{width: '100%'}}>*/}
                  {/*  <FormGroup>*/}
                  {/*    <label htmlFor="">*/}
                  {/*      <small>{t<string('picture_name')}</small>*/}
                  {/*    </label>*/}
                  {/*    <TextField*/}
                  {/*      size={'sm'}*/}
                  {/*      onChange={e =>*/}
                  {/*        setKeyValuePairs({picture_name: e.target.value})*/}
                  {/*      }*/}
                  {/*      placeholder={t<string('picture_name')}*/}
                  {/*      className={'form-control'}*/}
                  {/*    />*/}
                  {/*  </FormGroup>*/}
                  {/*  <FormGroup>*/}
                  {/*    <label htmlFor="">*/}
                  {/*      <small>{t<string('door_assembly')}</small>*/}
                  {/*    </label>*/}
                  {/*    <TextField*/}
                  {/*      size={'sm'}*/}
                  {/*      onChange={e =>*/}
                  {/*        setKeyValuePairs({door_assembly: e.target.value})*/}
                  {/*      }*/}
                  {/*      placeholder={t<string('door_assembly')}*/}
                  {/*      className={'form-control'}*/}
                  {/*    />*/}
                  {/*  </FormGroup>*/}
                  {/*</div>*/}
                  <div className={`col-md-12 ${styles.addToCart}`}>
                    <div className={`${styles.productQuantity}`}>
                      <label htmlFor="">{t<string>('quantity')}:</label>
                      <QuantityField
                        onValueChanged={value => {
                          setQuantity(value);
                        }}
                        initialValue={1}
                      />
                    </div>
                    <Button
                      disabled={
                        addItemLoading || !product?.id || load_product_prices
                      }
                      onClick={handleAddToCart}
                    >
                      {t<string>('add_to_cart')}{' '}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`${styles.productDetail} row`}
            style={{backgroundColor: '#fff'}}
          >
            <div className="col-md-7">
              <ProductDisplayTab
                productName={product_name}
                productGroupUID={data.uid}
                images={images.slice(1, 5)}
                content_container={content_container}
                tabs={tabs}
              />
            </div>
            <div className="col-md-5">
              <div className={`${styles.productDescription}`}>
                <h3>{t<string>('warranty')}</h3>
                <div
                  className={`${styles.productDescriptionContent}`}
                  dangerouslySetInnerHTML={{
                    __html: product_group_description,
                  }}
                ></div>
                <div
                  className={`${styles.productDescriptionContent}`}
                  dangerouslySetInnerHTML={{
                    __html: product_group_information,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
